import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Input } from 'antd';

import { useUserClientNote } from './useUserClientNote';

import type { FC } from 'react';

import './UserClientNote.scss';

interface UserClientNoteProps {
  note: string | null;
}

// Client note in the lead card details: always shown, with an explicit empty
// state, and edited in place when the user may edit the client.
export const UserClientNote: FC<UserClientNoteProps> = ({ note }) => {
  const {
    trimmedNote,
    canEdit,
    isEditing,
    isSaving,
    draft,
    draftChangeHandler,
    startEditHandler,
    cancelEditHandler,
    saveHandler,
    keyDownHandler,
  } = useUserClientNote({ note });

  return (
    <div className="UserClientNote" data-testid="lead-client-note">
      <div className="UserClientNote__header">
        <span className="UserClientNote__title">Примечание</span>
        {canEdit && trimmedNote && !isEditing && (
          <Button
            type="text"
            size="small"
            className="UserClientNote__edit"
            icon={<EditOutlined />}
            aria-label="Изменить примечание"
            data-testid="lead-client-note-edit"
            onClick={startEditHandler}
          />
        )}
      </div>

      {isEditing && (
        <div className="UserClientNote__form">
          <Input.TextArea
            className="UserClientNote__textarea"
            autoSize={{ minRows: 2, maxRows: 6 }}
            value={draft}
            placeholder="Введите примечание"
            disabled={isSaving}
            autoFocus
            data-testid="lead-client-note-input"
            onChange={({ target: { value } }) => draftChangeHandler(value)}
            onKeyDown={keyDownHandler}
          />
          <div className="UserClientNote__actions">
            <Button
              size="small"
              disabled={isSaving}
              onClick={cancelEditHandler}
            >
              Отмена
            </Button>
            <Button
              type="primary"
              size="small"
              loading={isSaving}
              data-testid="lead-client-note-save"
              onClick={() => void saveHandler()}
            >
              Сохранить
            </Button>
          </div>
        </div>
      )}

      {!isEditing && trimmedNote && (
        <div className="UserClientNote__text">{trimmedNote}</div>
      )}

      {!isEditing && !trimmedNote && (
        <div className="UserClientNote__empty">
          <span className="UserClientNote__placeholder">
            Примечание отсутствует
          </span>
          {canEdit && (
            <Button
              type="link"
              size="small"
              className="UserClientNote__add"
              icon={<PlusOutlined />}
              data-testid="lead-client-note-add"
              onClick={startEditHandler}
            >
              Добавить
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
